import { cn } from "@/lib/format";

type Props = {
  inclusions?: string;
  exclusions?: string;
};

/**
 * "Inclusions/Exclusions" block — matches production's real markup exactly
 * (confirmed via the live DOM): one shared heading over the two CMS-authored
 * HTML lists back to back, with check / cross markers applied via the
 * `cost-list` CSS classes (see globals.css). Production has no separate
 * "What's included" / "What's not included" sub-headings.
 *
 * Content comes from `pricing.tiers[key].costInclude` / `costExclude` — the
 * top-level `content.inclusions` / `content.exclusions` fields are empty on
 * this backend for every package checked.
 *
 * The HTML must already be sanitized (`withSanitizedTiers`): this renders
 * inside the client PackageTiers switcher, so it can't call the sanitizer.
 */
export function CostDetails({ inclusions, exclusions }: Props) {
  if (!inclusions && !exclusions) return null;

  return (
    <div className="mt-6">
      {inclusions ? (
        <>
          <h3 className="mb-3 flex items-center font-secondary text-[1.125rem] font-semibold text-headings md:text-xl">
            Inclusions
          </h3>

          <SanitizedHtml
            html={inclusions}
            className="cost-list text-text prose-invert mb-1.5 cost-list--in font-medium"
          />
        </>
      ) : null}
      {exclusions ? (
        <>
          <h3 className="mb-3 mt-6 flex items-center font-secondary text-[1.125rem] font-semibold text-headings md:text-xl">
            Exclusions
          </h3>
          <SanitizedHtml
            html={exclusions}
            className="cost-list text-text prose-invert cost-list--out font-medium"
          />
        </>
      ) : null}
    </div>
  );
}

function SanitizedHtml({ html, className }: { html: string; className: string }) {
  return (
    <div
      className={cn("prose", className)}
      dangerouslySetInnerHTML={{ __html: html }}
    />
  );
}
